/**
 * Auth context — single source of truth for "who am I" across the SPA.
 *
 * States:
 *   user === undefined   → still checking (initial mount)
 *   user === null        → confirmed unauthenticated
 *   user === { ... }     → authenticated
 *
 * Every request goes through the configured `api` axios instance from
 * lib/api.js — that instance carries the 401→/auth/refresh interceptor,
 * so hard page reloads with an expired access cookie (but live refresh
 * cookie) transparently mint a fresh access token instead of bouncing
 * the user to /login.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthContext = createContext({
  user: undefined,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

/** Map any FastAPI error payload to a user-facing string. FastAPI 422
 *  validation returns `detail: [{msg,...}, ...]` and rendering that array
 *  directly crashes React.
 *
 *  Most callers pass the raw axios error object (`e`), so we transparently
 *  unwrap `e?.response?.data?.detail` (FastAPI's standard error shape) before
 *  formatting — this is what makes 429 / 400 / 422 backend messages reach
 *  the user-facing toast. Falls back to `e.message` for network failures
 *  that never produced a response body. */
export function formatApiErrorDetail(detail) {
  // Unwrap axios-style errors so callers can pass the raw caught error.
  if (detail && typeof detail === "object" && detail.isAxiosError) {
    const data = detail.response?.data;
    if (data && typeof data === "object" && "detail" in data) {
      return formatApiErrorDetail(data.detail);
    }
    if (typeof data === "string" && data.trim()) return data;
    if (typeof detail.message === "string" && detail.message) return detail.message;
  }
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // `undefined` = still checking, `null` = unauthenticated, object = auth'd.
  const [user, setUser] = useState(undefined);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout", {});
    } catch {
      // Logout is best-effort — clearing client state below is what matters.
    }
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    await api.post("/auth/refresh", {});
    return fetchMe();
  }, [fetchMe]);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
